import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { LOGO_SRC, SITE_EMAIL, SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from '../../lib/branding';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Download Catalog', path: '/download-catalog' },
    { name: 'Contact', path: '/contact' },
  ];

  const categories = [
    { name: 'Perfume', path: '/category/perfume' },
    { name: 'Deodorant', path: '/category/deodorant' },
    { name: 'Body Mist', path: '/category/body-mist' },
    { name: 'Gift Set', path: '/category/gift-set' },
    { name: 'Attar', path: '/category/attar' },
    { name: 'Cosmetics', path: '/category/cosmetics' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'FAQ', path: '/faq' },
  ];

  return (
    <footer className="charcoal-gradient text-white" data-testid="main-footer">
      {/* Silver Separator */}
      <div className="h-1 silver-gradient" />

      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <img
                src={LOGO_SRC}
                alt="Silver Perfumes"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Authorized wholesaler & distributor of premium fragrances since 2013. 
              Ahmedabad's largest exporter of perfumes, deodorants, and cosmetics.
            </p>
            <div className="silver-separator" />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#C0C4CC] uppercase tracking-[0.15em] text-xs font-medium mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#C0C4CC] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-[#C0C4CC] uppercase tracking-[0.15em] text-xs font-medium mb-6">
              Categories
            </h4>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.name}>
                  <Link
                    to={cat.path}
                    className="text-gray-300 hover:text-[#C0C4CC] transition-colors text-sm"
                    data-testid={`footer-category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[#C0C4CC] uppercase tracking-[0.15em] text-xs font-medium mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#C0C4CC] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300 text-sm">
                  Ahmedabad, Gujarat<br />India
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#C0C4CC] flex-shrink-0" />
                <a 
                  href={`tel:${SITE_PHONE_TEL}`} 
                  className="text-gray-300 hover:text-[#C0C4CC] transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  {SITE_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#C0C4CC] flex-shrink-0" />
                <a 
                  href={`mailto:${SITE_EMAIL}`} 
                  className="text-gray-300 hover:text-[#C0C4CC] transition-colors text-sm"
                  data-testid="footer-email"
                >
                  {SITE_EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#C0C4CC] flex-shrink-0" />
                <span className="text-gray-300 text-sm">
                  Mon - Sat: 10:00 AM - 7:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} Silver Perfumes. All rights reserved.
            </p>
            <div className="flex gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-gray-400 hover:text-[#C0C4CC] transition-colors text-sm"
                  data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
